import { chakra } from '@chakra-ui/react';
import { Check, Minus } from 'lucide-react';
import { forwardRef, useEffect, useRef, type ReactNode } from 'react';

type CheckboxSize = 'sm' | 'md';
const boxSizes: Record<CheckboxSize, string> = { sm: '4', md: '5' };

const Root = chakra('label', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2.5',
    fontFamily: 'body',
    fontSize: 'md',
    color: 'text.primary',
    cursor: 'pointer',
    userSelect: 'none',
    '&:has(input:disabled)': { cursor: 'not-allowed', color: 'text.muted', opacity: 0.7 },
    '& input:checked + span, & input:indeterminate + span': { opacity: 1 },
  },
});

const Control = chakra('input', {
  base: {
    appearance: 'none',
    boxSize: 'full',
    margin: 0,
    borderWidth: '1px',
    borderColor: 'border.strong',
    borderRadius: 'sm',
    bg: 'surface.default',
    cursor: 'inherit',
    transitionProperty: 'background-color, border-color, box-shadow',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    outline: 'none',
    _hover: { borderColor: 'action.primary' },
    _checked: { bg: 'action.primary', borderColor: 'action.primary' },
    _indeterminate: { bg: 'action.primary', borderColor: 'action.primary' },
    _focusVisible: { boxShadow: 'focus', borderColor: 'action.primary' },
    _disabled: { _hover: { borderColor: 'border.strong' } },
    '&[aria-invalid="true"]': { borderColor: 'status.danger' },
  },
});

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'color'> {
  size?: CheckboxSize;
  label?: ReactNode;
  invalid?: boolean;
  /** Mixed/partial selection state (visual dash, sets input.indeterminate). */
  indeterminate?: boolean;
}

/** A single checkbox with an optional inline label. Native input → full a11y. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { size = 'md', label, invalid, indeterminate = false, disabled, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <Root>
      <chakra.span position="relative" display="inline-flex" boxSize={boxSizes[size]} flexShrink={0}>
        <Control
          type="checkbox"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          ref={(node: HTMLInputElement | null) => {
            innerRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          {...rest}
        />
        <chakra.span
          aria-hidden
          position="absolute"
          inset="0"
          display="grid"
          placeItems="center"
          color="text.inverse"
          opacity={0}
          pointerEvents="none"
        >
          {indeterminate ? <Minus size="80%" strokeWidth={3} /> : <Check size="80%" strokeWidth={3} />}
        </chakra.span>
      </chakra.span>
      {label != null && <span>{label}</span>}
    </Root>
  );
});
